import { ROLE } from 'services/assist-api/entities/role/roles';
import { hasRole } from './helpers';
import MarkPolicy from './mark.policy';
import MarkingAllocationPolicy from './marking-allocation.policy';
import MarkingGroupPolicy from './marking-group.policy';
import MarkingTaskPolicy from './marking-task.policy';
import type { AuthorisableUser } from './types';

/**
 * A set of routes that can be authorised in Assist. All restricted React Router routes and main
 * menu items should be associated with one of these routes so that the authorisation rules for
 * accessing a specific page or viewing a main menu item can be centralised in this Route Policy
 * and not duplicated in both React Router route files and the Main Menu.
 *
 * Note, these routes do not necessarily correlate 1:1 with React Router routes defined in Assist
 * since React Router allows for route grouping. But the intent should be to keep these as close as
 * possible to the route format used by React Router. We may refactor further at a later point in
 * time to make these 1:1.
 */
export enum AuthorisableRoute {
  Dashboard = '/',
  DashboardSubpages = '/product/*',
  AreaUsersWildcard = '/organisation-area-users/*',
  ReportsWildcard = '/reports/*',
  SystemWideReportsWildcard = '/system-wide-reports/*',
  MarkingWildcard = '/marking/*',
  MarkingAllocationPage = '/marking/marker-allocation',
  MarksForReviewPage = '/marking/marks-for-review',
  MarkingGroupsWildcard = '/marking/marking-groups/*',
  MarkResponsePage = '/marking/mark-response',
  ClustersWildcard = '/clusters/*',
  ClassesWildcard = '/classes/*',
  AdminWildcard = '/admin/*',
  UsersWildcard = '/organisation-users/*',
  UserManagementSubpages = '/organisation-users/manage/*', // Also used for non-"manage/*" routes.
  SubscriptionsWildcard = '/subscriptions/*',
  OrganisationManagementSubpages = '/organisations/:orgId*' // Also used for the create org route.
}

/**
 * Authorisation rules for the resource that the policy is named after.
 * Functions return true only if the user can perform the action.
 * Follows the policy pattern used in Gate (Laravel) and Pundit (Rails).
 */
export default class RoutePolicy {
  static view(user: AuthorisableUser, route: AuthorisableRoute): boolean {
    switch (route) {
      case AuthorisableRoute.Dashboard: // Dashboard home page
      case AuthorisableRoute.ReportsWildcard:
        return hasRole(user, [ROLE.SITE_ADMIN, ROLE.ADMIN, ROLE.TEACHER, ROLE.LEARNER]);
      case AuthorisableRoute.SystemWideReportsWildcard:
        return hasRole(user, [ROLE.SITE_ADMIN]);
      case AuthorisableRoute.MarkingWildcard:
        return MarkingTaskPolicy.list(user);
      case AuthorisableRoute.MarkingAllocationPage:
      case AuthorisableRoute.MarksForReviewPage:
        return MarkingAllocationPolicy.allocate(user);
      case AuthorisableRoute.MarkingGroupsWildcard:
        return MarkingGroupPolicy.list(user);
      case AuthorisableRoute.MarkResponsePage:
        return MarkPolicy.viewPage(user);
      case AuthorisableRoute.ClustersWildcard:
      case AuthorisableRoute.ClassesWildcard:
      case AuthorisableRoute.UsersWildcard:
      case AuthorisableRoute.SubscriptionsWildcard:
      case AuthorisableRoute.DashboardSubpages:
        return hasRole(user, [ROLE.SITE_ADMIN, ROLE.ADMIN, ROLE.TEACHER]);
      case AuthorisableRoute.AreaUsersWildcard:
        return hasRole(user, [ROLE.SITE_ADMIN, ROLE.ADMIN, ROLE.PRODUCT_ADMIN]);
      case AuthorisableRoute.AdminWildcard:
      case AuthorisableRoute.UserManagementSubpages:
      case AuthorisableRoute.OrganisationManagementSubpages:
        return hasRole(user, [ROLE.SITE_ADMIN, ROLE.ADMIN]);
      default:
        return false;
    }
  }
}
